/**
 * Format a price in cents to a display string.
 * e.g., 7500 → "$75", 25099 → "$250.99"
 */
export function formatPrice(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/**
 * Format a date string to a readable event date.
 * e.g., "Sunday, May 24, 2026"
 */
export function formatEventDate(dateStr: string): string {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(dateStr));
}

/**
 * Format a date string to event time range.
 * e.g., "5:00 PM – 10:00 PM"
 */
export function formatEventTime(
  startStr: string,
  endStr: string | null
): string {
  const fmt = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const start = fmt.format(new Date(startStr));
  if (!endStr) return start;
  const end = fmt.format(new Date(endStr));
  return `${start} – ${end}`;
}

/**
 * Calculate remaining quantity for a tier/package.
 */
export function remainingQuantity(available: number, sold: number): number {
  return Math.max(0, available - sold);
}

/**
 * Site-wide kill switch. When VITE_SALES_CLOSED=true every tier and table
 * package renders as Sold Out regardless of inventory. Unset the var (or set
 * to anything other than "true") to reopen sales.
 */
export const SALES_CLOSED = import.meta.env.VITE_SALES_CLOSED === "true";

/**
 * Check if an item is sold out.
 */
export function isSoldOut(available: number, sold: number): boolean {
  if (SALES_CLOSED) return true;
  return remainingQuantity(available, sold) <= 0;
}
